<script lang="ts">
import { lateinit } from 'tg-commons'
import { bindEvents, type LatLng, type PolygonEventMap, type PolygonOptions, type PolygonOverlay } from 'tg-map-core'
import { defineComponent, toRaw, type PropType } from 'vue'
import { eventProp, optionalProp, requiredProp, useSplittedAttrs, type EventEmits, type EventProps, type Props } from '../../utils/vue-utils'
import MapMixin from '../map-mixin'

type TgPolygonEmits = {
  'update:paths': LatLng[][],
}

export const tgPolygonProps = {
  paths: requiredProp(Array as PropType<LatLng[][]>),
  clickable: optionalProp(Boolean),
  editable: optionalProp(Boolean),
  strokeColor: optionalProp(String),
  strokeOpacity: optionalProp(Number),
  strokeWeight: optionalProp(Number),
  visible: optionalProp(Boolean),
  zIndex: optionalProp(Number),
  fillColor: optionalProp(String),
  fillOpacity: optionalProp(Number),
  'onUpdate:paths': eventProp(),
} satisfies Props<PolygonOptions> & EventProps<TgPolygonEmits>

export type TgPolygonEventEmits = EventEmits<PolygonEventMap & TgPolygonEmits>

export default defineComponent({
  name: 'tg-polygon',
  mixins: [MapMixin],
  inheritAttrs: false,
  props: tgPolygonProps,
  emits: undefined as any as TgPolygonEventEmits,
  setup(props, { attrs }) {
    return {
      attrs: useSplittedAttrs(attrs, props),
      overlay: lateinit<PolygonOverlay>(),
      emittedPaths: undefined as LatLng[][] | undefined,
    }
  },
  watch: {
    paths: {
      handler(value: LatLng[][]) {
        if (this.emittedPaths != null) {
          const paths = this.emittedPaths
          this.emittedPaths = undefined
          // 由于paths数组绝大多数时候长度都是1, 故这里可以浅层比较它
          if (paths.length === value.length && paths.every((v, i) => v === toRaw(value[i]))) {
            // 防止emit出的paths被设置回来
            return
          }
        }
        this.overlay.setPaths(value)
      },
      deep: true,
    },
    clickable() {
      this.recreate()
    },
    editable(value) {
      this.overlay.setEditable(value)
    },
    strokeColor(value) {
      this.overlay.setStrokeColor(value)
    },
    strokeOpacity(value) {
      this.overlay.setStrokeOpacity(value)
    },
    strokeWeight(value) {
      this.overlay.setStrokeWeight(value)
    },
    fillColor(value) {
      this.overlay.setFillColor(value)
    },
    fillOpacity(value) {
      this.overlay.setFillOpacity(value)
    },
    visible(value) {
      this.overlay.setVisible(value)
    },
    zIndex(value) {
      this.overlay.setZIndex(value)
    },
  },
  onCreate() {
    this.overlay = this.$map.createPolygon(this)
    this.$map.addOverlay(this.overlay)
    bindEvents(this.attrs.listeners, this.overlay, this.$emit)
    if (this.attrs.listenerProps['update:paths']) {
      this.overlay.addEventListener('paths-edited', () => {
        this.$emit('update:paths', this.emittedPaths = this.overlay.getPaths())
      })
    }
  },
  onDestroy() {
    this.$map.removeOverlay(this.overlay)
  },

})
</script>
